import update from 'immutability-helper';
import get from 'lodash/get';
import { METRICS } from '@utils/metrics';
import { LAYOUT_MAP, MARIO, CURRENT_PRODUCT_MODE, isAppModule } from '@utils/constants';
import { summaryCardFlag, diagnosticGraphFlag, metricPanelFlag } from '@utils/collapseConstants';
import * as drilldownTypes from '@store/summaryContainer/drilldown/types';
import * as comparisonTypes from '@store/comparison/types';
import featureNames from '@utils/featureToggle/constants';
import { isGrandfatherFilter, getPresetFid } from '@utils';
import getGranularity, { getAppDefaultGranularity } from '@utils/granularity';
import * as compareDrawerTypes from '@store/compareDrawer/types';
import { DEFAULT_FILTER_COLOR } from '@utils/colorPalette';
import { allowedGranularities } from '@components/common/GranularitySelect/utils';
import * as types from './types';

export const initialFilter = {
  name: 'All Traffic',
  rule: [],
};

const allMetricKeys = Object.keys(METRICS).reduce((total, name) => {
  total.push(...Object.keys(METRICS[name as keyof typeof METRICS]));
  return total;
}, [] as string[]);

export const initialState = {
  appliedFilters: [initialFilter],
  dimensionTypeInFilter: [],
  metricsToNA: [], // contains all supported metrics that are not applicable for current applied filter
  isGrandfatherFilter: false,
  filterColor: DEFAULT_FILTER_COLOR,
  compareMode: 0,
  requestsSent: 0,
  responsesReceived: 0,
  showMask: false,
  dashboard: {
    list: [
      {
        id: 'trends',
        name: 'Trends',
        context: '',
      },
      // {
      //   id: 'all-metrics',
      //   name: 'All Metrics',
      // },
    ],
    activeId: 'trends',
  },
  interval: { from: '', to: '', shortcut: '', granularity: '' },
  isLiveModeSelected: false,
  resetDiagnostics: false,

  /* -- initialze - InitData START */
  dashboardTypes: {},
  profiles: [],
  aiAlertsSensitivity: [],
  aiAlertsMetricsMeta: [],
  features: {
    // Following features have pre-determined defaults. Features API response determines
    // the customer specific behavior of the feature
    [featureNames.ERROR_DIAGNOSTICS]: false,
    [featureNames.DAY_OVER_DAY]: false,
    [featureNames.SAVED_DASHBOARDS]: false,
    [featureNames.SHARE]: false,
    [featureNames.COMPARE]: true,
    [featureNames.COMBINATORIAL_FILTER]: true,
    [featureNames.TRENDS_DIAGNOSTICS]: true,
    [featureNames.MINUTE_GRANULARITY]: true,

    [featureNames.CREATE_FILTER]: true,
    [featureNames.SECONDARY_TIME_SERIES]: true,
    [featureNames.TRENDS_DIAG_COMPARE_SERIES]: true,
    [featureNames.EXPORT_DATA]: true,
    [featureNames.NOC]: false,
    [featureNames.NOC_AD_HOC]: false,
    [featureNames.NOC_SHARE]: false,
    [featureNames.NOC_SAVE]: false,
    [featureNames.REALTIME_MAX_FILTERS]: 20,
    [featureNames.REALTIME_ROWS_PER_DIMENSION]: 200,
    [featureNames.REALTIME_AI_THRESHOLD]: true,

    [featureNames.ML]: false,

    [featureNames.AI_ROADMAP]: false,

    [featureNames.VIEWER_DRILLDOWN]: false,
    [featureNames.VIEWER_DRILLDOWN_DISTRIBUTION]: false,
    [featureNames.VIEWER_DRILLDOWN_REST_METRICS]: false,
    [featureNames.DISTRIBUTION_METRICS]: false,
    [featureNames.DISTRIBUTION_METRICS_DRILLDOWN]: true,

    // Limits for the following features are set to a high value by default.
    // These limits, in practice, will never be reached. Features API response
    // replaces high limits with actual limits
    [featureNames.ENUMERATED_VALUES_PER_DIMENSION]: 200,
    [featureNames.ERROR_CODES_LIMIT]: 200,
    [featureNames.DATA_ACCESS_DURATION]: 24, // months
    [featureNames.ALL_METRICS]: allMetricKeys,
    [featureNames.METRICS]: Object.keys(METRICS[CURRENT_PRODUCT_MODE]),
    [featureNames.DRILLDOWN_LIMIT]: 3,
    [featureNames.CUSTOM_TAGS]: null,
    [featureNames.EXPORT_DATA]: true,
    [featureNames.EXPORT_WIDGET_DATA]: true,
    [featureNames.VIEWER_DRILLDOWN_TIMELINE]: false,
    [featureNames.CUSTOMER_ACCOUNT_TYPE]: 0,
    [featureNames.ALERT_DIAGNOSTICS]: false,

    [featureNames.NON_BLOCKING_LOADER]: false,
    [featureNames.TOP_RISING_CONTENT]: false,

    [featureNames.NEW_TRENDS_LAYOUT]: false,
    [featureNames.NOC_BUSINESS_ERROR_METRICS]: false,

    [featureNames.EXPERIENCE_DASHBOARD]: false,
    [featureNames.EXPERIENCE_DASHBOARD_BENCHMARK]: false,
    [featureNames.EXPERIENCE_DASHBOARD_ADVANCED_BENCHMARK]: false,
    [featureNames.AUTO_RETRY]: false,
    [featureNames.OVB_SHARE]: false,
    [featureNames.OVB_SAVE]: false,
    [featureNames.UNIFIED_AD_CONTENT_OVERVIEW]: false,
    [featureNames.SI_BIGPANDA]: false,
    [featureNames.SI_DATADOG]: false,
    [featureNames.EI_PAGERDUTY]: false,
    [featureNames.METRIC_SELECTOR_POPUP]: false,
    [featureNames.AI_ALERTS_PULSE5]: false,
    [featureNames.APP_AI_ALERTS]: false,
    [featureNames.AI_ALERTS_1_MIN_PERSISTENCE]: false,
    [featureNames.AI_ALERTS_CONFIG]: false,
    [featureNames.AI_ALERTS_ADD_CUSTOM_DIMENSIONS]: false,
    [featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT_LEVEL_1]: 0,
    [featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT_LEVEL_2]: 0,
    [featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT_LEVEL_3]: 0,

    [featureNames.SESSION_TIMELINE_30DAYS_SAVE]: false,
    [featureNames.PRECISION_POLICY_SCHEDULING]: false,
    // viewer module
    [featureNames.ADVANCED_VIEWER_MODULE_ENABLED]: false,
    [featureNames.ENABLE_2019_CHANGES]: false,
    [featureNames.SHOW_NINETY_DAYS_HISTORY]: false,
    [featureNames.VIEWER_MODULE_PULSE5]: false,
    [featureNames.AUTO_INSIGHTS]: false,
    [featureNames.AI_ALERTS_PCP]: false,
    [featureNames.AI_ALERTS_CIRR]: false,
    [featureNames.VIEWERS_OVERVIEW]: false,
    [featureNames.VIEWERS_BROWSER_TAB]: false,
    [featureNames.KPI_DROPDOWN]: false,
    [featureNames.TIME_GRANULARITY]: false,
    [featureNames.ANNOTATIONS]: false,
    [featureNames.HORIZONTAL_LAYOUT]: false,
    [featureNames.PUBLIC_SAVED_DASHBOARDS]: false,
    [featureNames.REALTIME_PSD]: false,
    [featureNames.RT_ON_TRENDS_LAST_15]: false,
    [featureNames.VM_LIVE_TLB]: false,
    [featureNames.PROMPT]: false,
    // ECO
    [featureNames.APP_EXPERIENCE]: false,
    [featureNames.APP_USER_TIMELINE]: false,
    [featureNames.APP_EXPERIENCE_BACKEND]: 'mario',
    [featureNames.APP_RT_ON_TRENDS]: true,
    [featureNames.APP_INSIGHTS_METRIC_FILTERING]: false,
    [featureNames.APP_INSIGHTS_DISTRIBUTION]: false,
    [featureNames.APP_EXPERIENCE_SAVE_AND_SHARE]: false,
    [featureNames.APP_WEBHOOKS]: false,
    [featureNames.APP_SPI]: false,
    [featureNames.APP_PAGERDUTY]: false,
    [featureNames.AI_ALERTS_CUSTOM_METRICS]: false,
    [featureNames.AI_ALERTS_CUSTOM_METRICS_UPPER_LIMIT]: 0,
    [featureNames.ECO_SANKEY]: false,
    [featureNames.ECO_FUNNEL]: false,
  },
  /* -- initialze - InitData END */
  collapseStatus: {
    [metricPanelFlag]: false,
    [summaryCardFlag]: false,
    [diagnosticGraphFlag]: false,
    [featureNames.VIEWER_DRILLDOWN_DISTRIBUTION]: false,
  },
  isSharingPage: false,
  layout: LAYOUT_MAP.horizontal,
  userDefinedMetrics: {},
  appBackendType: MARIO,
  aiAlertsDimensionMapping: {},
};

const reducerMap = Object.create(null);

reducerMap[drilldownTypes.SELECT_COMPARE_MODE] = (state, action) =>
  update(state, {
    compareMode: {
      $set: action.mode,
    },
  });

reducerMap[types.RESET] = (state) =>
  update(state, {
    $merge: {
      appliedFilters: initialState.appliedFilters,
      resetDiagnostics: false,
      collapseStatus: initialState.collapseStatus,
    },
  });

reducerMap[types.SET_COMPARE_MODE] = reducerMap[drilldownTypes.SELECT_COMPARE_MODE];

reducerMap[comparisonTypes.MODIFY_COMPARISON] = (state, action) => {
  const { interval, appliedFilters } = action.config;
  const { resetDiagnostics } = action;
  const granularity = interval.granularity || getGranularity(interval.from, interval.to);

  return update(state, {
    compareMode: { $set: action.mode },
    interval: { $set: { ...interval, granularity } },
    resetDiagnostics: {
      $set: resetDiagnostics,
    },
    $merge: {
      appliedFilters,
    },
  });
};

reducerMap[compareDrawerTypes.EXIT_COMPARE_MODE] = (state) =>
  update(state, {
    compareMode: {
      $set: 0,
    },
  });

reducerMap[types.SET_REAL_TIME_MODE] = (state, action) =>
  update(state, {
    isLiveModeSelected: {
      $set: action.flag,
    },
  });

reducerMap[types.INTERVAL_SELECTION] = (state, action) => {
  let granularity = getGranularity(action.from, action.to, true, false, action.shortcut);
  if (isAppModule) {
    granularity = getAppDefaultGranularity(
      action.from,
      action.to,
      true,
      false,
      action.shortcut,
      action.dashboardType,
    );
  }
  return update(state, {
    interval: {
      $set: {
        from: action.from,
        to: action.to,
        shortcut: action.shortcut,
        granularity: granularity,
      },
    },
  });
};
reducerMap[types.UPDATE_GRANULARITY] = (state, action) =>
  update(state, {
    interval: {
      $merge: {
        granularity: action.granularity,
      },
    },
  });

reducerMap[types.CHANGE_IS_SHARING_PAGE] = (state, action) =>
  update(state, {
    isSharingPage: {
      $set: action.isSharingPage,
    },
  });

reducerMap[types.UPDATE_PROFILES] = (state, action) => {
  const { profiles } = action;
  const tzOffset = get(
    profiles.filter((p) => p.is_selected),
    '0.timezone',
  );
  // const features = get(profiles.filter(p => p.is_selected), '0.features');
  return update(state, {
    $merge: {
      profiles,
      tzOffset,
      // features,
    },
  });
};

reducerMap[types.UPDATE_FEATURES] = (state, action) => {
  const { features } = action;
  return update(state, {
    features: {
      $merge: {
        ...features,
      },
    },
  });
};

reducerMap[types.UPDATE_APP_ALL_METRICS] = (state, action) => {
  const { appAllMetrics } = action;
  return update(state, {
    features: {
      $merge: { app_metrics: appAllMetrics },
    },
  });
};

reducerMap[types.SET_DIMENSION_TYPE_IN_FILTER] = (state, action) => {
  const { data } = action;
  return update(state, {
    dimensionTypeInFilter: {
      $set: data,
    },
  });
};

reducerMap[types.UPDATE_USER_DEFINED_METRICS] = (state, action) =>
  update(state, {
    userDefinedMetrics: {
      $set: action.userDefinedMetrics,
    },
  });

reducerMap[types.SET_AI_ALERTS_SENSITIVITY] = (state, action) => {
  return update(state, {
    aiAlertsSensitivity: { $set: action.payload },
  });
};

reducerMap[types.SET_AI_ALERTS_METRICS_META] = (state, action) => {
  return update(state, {
    aiAlertsMetricsMeta: { $set: action.payload },
  });
};

reducerMap[types.APPLY_FILTER] = (state, action) => {
  const { filter } = action;
  const { name, rule = [], fid, is_advanced: filterIsAdvanced, isAdvanced } = filter;
  const { features } = state;
  const grandfatherFilter = isGrandfatherFilter(filter, features[featureNames.CUSTOM_TAGS]);

  const extraParams = {};
  // refresh key for reloading trends
  if ('_rKey' in filter) {
    extraParams['_rKey'] = filter['_rKey'];
  }

  // use passed in preset or check for a preset filter match
  const presetFid = filter.presetFid || getPresetFid(rule);

  return update(state, {
    $merge: {
      appliedFilters: [
        {
          name,
          rule,
          fid,
          isAdvanced: filterIsAdvanced || isAdvanced,
          presetFid,
          ...extraParams,
        },
      ],
      isGrandfatherFilter: grandfatherFilter,
    },
  });
};

reducerMap[types.ASYNC_HTTP_REQ_STARTED] = (state) => {
  const count = state.requestsSent + 1;
  return update(state, {
    $merge: {
      requestsSent: count,
      showMask: true,
    },
  });
};

reducerMap[types.ASYNC_HTTP_REQ_ENDED] = (state) => {
  const count = state.responsesReceived + 1;
  const showMask = state.requestsSent - count !== 0;
  if (showMask) {
    return update(state, {
      $merge: {
        responsesReceived: count,
        showMask,
      },
    });
  }
  return update(state, {
    $merge: {
      requestsSent: 0,
      responsesReceived: 0,
      showMask,
    },
  });
};

reducerMap[types.CHANGE_ACTIVE_DASHBOARD_ID] = (state, action) =>
  update(state, {
    dashboard: {
      activeId: {
        $set: action.dashboardId,
      },
    },
  });

reducerMap[types.ADD_NEW_DASHBOARD] = (state, action) => {
  const { dashboard } = state;
  const { newDashboard } = action;
  const last = dashboard[dashboard.length - 1];

  if (last.isNew === true) {
    return state;
  }
  newDashboard.isNew = true;
  return update(state, {
    dashboard: {
      list: {
        $push: [newDashboard],
      },
    },
  });
};

reducerMap[types.TOGGLE_MODAL] = (state, action) => {
  const { modalType } = action;
  const modalConfig = {
    modalType,
  };
  return update(state, {
    modal: {
      $set: modalConfig,
    },
  });
};

reducerMap[types.SET_SHARING_STATE] = (state, action) => {
  // backward compatibility for old shared dashboards
  const { interval } = action.sharingState;

  // validate granularity
  if (interval.granularity) {
    const allowedValues = allowedGranularities(interval);
    if (!allowedValues.includes(interval.granularity)) {
      interval.granularity = null;
    }
  }
  interval.granularity = interval.granularity || getGranularity(interval.from, interval.to);

  return update(state, {
    $merge: {
      ...action.sharingState,
      interval,
    },
  });
};

reducerMap[types.RESET_DIAGNOSTICS] = (state) =>
  update(state, {
    resetDiagnostics: {
      $set: false,
    },
  });

reducerMap[types.CHANGE_COLLAPSE_STATUS] = (state, action) =>
  update(state, {
    collapseStatus: {
      [action.key]: {
        $set: !state.collapseStatus[action.key],
      },
    },
  });

reducerMap[types.OPEN_COLLAPSE] = (state, action) =>
  update(state, {
    collapseStatus: {
      [action.key]: {
        $set: false,
      },
    },
  });

reducerMap[types.CLOSE_COLLAPSE] = (state, action) =>
  update(state, {
    collapseStatus: {
      [action.key]: {
        $set: true,
      },
    },
  });

/* TODO: For future reference,
 * Use below id in the editor to find the occurences of it's relative references.
 * Editor search key: dashboard_types_UUE93ID
 */
reducerMap[types.UPDATE_DASHBOARD_TYPE] = (state, action) =>
  update(state, {
    dashboardType: {
      $set: action.dashboardTypeObj,
    },
  });

reducerMap[types.TOGGLE_LAYOUT] = (state, action) =>
  update(state, {
    layout: {
      $set: action.layout,
    },
  });

reducerMap[types.TOGGLE_ANNOTATIONS] = (state, action) =>
  update(state, {
    annotations: {
      $set: action.value,
    },
  });

reducerMap[types.AI_ALERTS_DIMENSION_MAPPING] = (state, action) =>
  update(state, {
    aiAlertsDimensionMapping: {
      $set: action.mapData,
    },
  });

/* eslint max-len:0 */
const common = (state = initialState, action) =>
  reducerMap[action.type] ? reducerMap[action.type](state, action) : state;

export default common;
